import { type ActorRefFrom, assertEvent, assign, sendParent, setup } from "xstate";

import type { PlayerId } from "@/types/branded";
import { computeRounds } from "../graphUtils";
import type { MatchId } from "../types";
import type { KnockoutGraph } from "../types/graph";
import type { KnockoutNode } from "../types/node";

// --- 2. The Match Machine (Worker) ---
const knockoutMatchMachine = setup({
  types: {
    context: {} as {
      node: KnockoutNode;
      players: [PlayerId | null | undefined, PlayerId | null | undefined];
      winner: PlayerId | undefined;
    },
    input: {} as { node: KnockoutNode },
    events: {} as
      | { type: "ASSIGN_PLAYER"; playerId: PlayerId; slot: 0 | 1 }
      | { type: "REPORT_RESULT"; winnerId: PlayerId },
    output: {} as { winner: PlayerId; players: [PlayerId, PlayerId] },
  },
}).createMachine({
  id: "match",
  initial: "waiting",

  context: ({ input }) => ({
    node: input.node,
    players: [undefined, undefined],
    winner: undefined,
  }),

  states: {
    waiting: {
      always: {
        target: "ready",
        guard: ({ context }) => context.players.every((p) => p !== undefined),
      },
      on: {
        ASSIGN_PLAYER: {
          actions: assign({
            players: ({ context, event }) => {
              const newPlayers = [...context.players] as [PlayerId | null, PlayerId | null];
              newPlayers[event.slot] = event.playerId;
              return newPlayers;
            },
          }),
        },
      },
    },

    ready: {
      on: {
        REPORT_RESULT: {
          actions: assign({ winner: ({ event }) => event.winnerId }),
          target: "completed",
        },
      },
    },

    completed: {
      type: "final",
      entry: sendParent(
        ({ context }) =>
          ({
            type: "MATCH_FINISHED",
            matchId: context.node.id,
            winnerId: context.winner ?? context.players.find((p) => p !== null)!,
            players: context.players as [PlayerId, PlayerId],
          }) satisfies KnockoutMatchFinishedEvent,
      ),
    },
  },

  output: ({ context }) => {
    return {
      winner: context.winner!,
      players: context.players as [PlayerId, PlayerId],
    };
  },
});

export type KnockoutMatchActorRef = ActorRefFrom<typeof knockoutMatchMachine>;

export type KnockoutContext = {
  players: PlayerId[];
  graph: KnockoutGraph;
  matchRefs: Map<MatchId, KnockoutMatchActorRef>;
  currentRound: number;
  pendingMatches: Set<MatchId>;
};

export type KnockoutMatchFinishedEvent = {
  type: "MATCH_FINISHED";
  matchId: MatchId;
  winnerId: PlayerId;
  players: [PlayerId, PlayerId];
};

// --- 4. The Tournament Machine (Manager) ---
export const knockoutMachine = setup({
  types: {
    context: {} as KnockoutContext,
    events: {} as KnockoutMatchFinishedEvent,
    input: {} as {
      players: PlayerId[];
      initialGraph: KnockoutGraph;
    },
    output: [] as PlayerId[][],
  },
  actors: { matchActor: knockoutMatchMachine },
  actions: {
    spawnMatches: assign(({ context, spawn }) => {
      const newRefs = new Map(context.matchRefs);
      context.graph.nodes.forEach((node, id) => {
        if (!newRefs.has(id)) {
          const ref = spawn("matchActor", { id, input: { node } });
          newRefs.set(id, ref);
        }
      });
      return { matchRefs: newRefs };
    }),

    applySeeds: ({ context }) => {
      context.graph.edges.forEach((edge) => {
        if (edge.kind !== "seed") return;

        const targetRef = context.matchRefs.get(edge.to);

        if (targetRef) {
          targetRef.send({ type: "ASSIGN_PLAYER", playerId: edge.player!, slot: edge.slot });
        }
      });
    },

    propagateResults: ({ context, event }) => {
      assertEvent(event, "MATCH_FINISHED");

      const outgoingEdges = context.graph.edges.filter(
        (e) => e.kind === "match" && e.from === event.matchId,
      );

      outgoingEdges.forEach((edge) => {
        if (edge.kind !== "match") return;
        const targetRef = context.matchRefs.get(edge.to);
        if (targetRef) {
          const playerToMove =
            edge.outcome === "winner"
              ? event.winnerId
              : event.players.find((p) => p !== event.winnerId)!;
          targetRef.send({ type: "ASSIGN_PLAYER", playerId: playerToMove, slot: edge.slot });
        }
      });
    },
  },
  guards: {
    isFinished: ({ context }) => {
      if (context.matchRefs.size === 0) return false;

      return Array.from(context.matchRefs.values()).every(
        (ref) => ref.getSnapshot().status === "done",
      );
    },
  },
}).createMachine({
  id: "tournamentManager",
  initial: "initializing",

  context: ({ input }) => ({
    players: input.players,
    graph: input.initialGraph,
    matchRefs: new Map(),
    pendingMatches: new Set(input.initialGraph.nodes.keys()),
    currentRound: 1,
  }),

  states: {
    initializing: {
      entry: ["spawnMatches", "applySeeds"],
      always: "playing",
    },

    playing: {
      on: {
        MATCH_FINISHED: {
          actions: ["propagateResults"],
          target: "evaluating",
        },
      },
    },

    evaluating: {
      always: [{ target: "finished", guard: "isFinished" }, { target: "playing" }],
    },

    finished: {
      type: "final",
    },
  },

  output: ({ context }) => {
    const roundsMap = computeRounds(context.graph);
    const matchesByRound = new Map<number, MatchId[]>();

    roundsMap.forEach((round, matchId) => {
      if (!matchesByRound.has(round)) matchesByRound.set(round, []);
      matchesByRound.get(round)!.push(matchId);
    });

    const tiers: PlayerId[][] = [];

    // Determine per player what their last round was, and for them in the last round, whether they won or lost
    const playerLastRound = new Map<PlayerId, { round: number; won: boolean }>();

    matchesByRound.forEach((matchIds, round) => {
      matchIds.forEach((matchId) => {
        const matchRef = context.matchRefs.get(matchId);
        if (!matchRef) return;

        const snapshot = matchRef.getSnapshot();
        if (snapshot.status !== "done") return;

        const { winner, players } = snapshot.output;
        const loser = players.find((p) => p !== winner);

        playerLastRound.set(winner, { round, won: true });
        if (loser) {
          playerLastRound.set(loser, { round, won: false });
        }
      });
    });

    // Group players by the last round they reached, and whether they won or lost in that round
    const tierMap = new Map<string, PlayerId[]>();

    playerLastRound.forEach(({ round, won }, playerId) => {
      const key = `${round}-${won ? "W" : "L"}`;
      if (!tierMap.has(key)) tierMap.set(key, []);
      tierMap.get(key)!.push(playerId);
    });

    // Sort tiers by round (descending), then winners before losers
    const sortedKeys = Array.from(tierMap.keys()).sort((a, b) => {
      const [roundA, resultA] = a.split("-");
      const [roundB, resultB] = b.split("-");
      if (roundA !== roundB) return parseInt(roundB!) - parseInt(roundA!); // Higher rounds first
      if (resultA !== resultB) return resultA === "W" ? -1 : 1; // Winners before losers
      return 0;
    });

    sortedKeys.forEach((key) => {
      tiers.push(tierMap.get(key)!);
    });

    return tiers;
  },
});
